/**
 * CloudStorageService
 * 
 * Service layer for Firebase Storage operations.
 * Matches Flutter's AppStorageService architecture.
 * 
 * Handles:
 * - Uploading files to Firebase Storage buckets
 * - Profile image management
 * - Cloud storage bucket operations
 * 
 * Note: Image processing (download, compression) delegated to LocalStorageService
 */

import {
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  type StorageReference,
  getStorage,
} from 'firebase/storage';
import { auth, storage } from '../../services/firebase';
// TODO: @claude add a follow up task for this: data→domain dependency-direction violation — data code must not
// import domain utils (.claude/context/conventions.md §Clean Architecture flow). Consumed at the single call site
// in uploadFileToUserProfilesBucket below. Pre-existing on main, NOT introduced by feat_edit_profile_image_upload.
// Scope the follow-up to sweep the sibling offenders too: cloudFunctionsService.ts:3 (FeedResponse) and
// firestoreService.ts:33 (AppTransactionType). appLogger below is a SANCTIONED exception (conventions.md §Logging).
// Neither fix is a drive-by: inlining `${env.USER_PROFILES_BUCKET_NAME}%2F${uid}` here duplicates a cross-client
// contract that Flutter substring-tests (User.profileImageUploaded, lib/data/models/user.dart:108-110), and moving
// the seam up to UserService.uploadUserProfileImage changes this method's contract for its other callers
// (syncSecureStorageUseCase.syncUploadProfileImage). See workflow/architecture_branch_reviews/
// feat_edit_profile_image_upload_arch_review/finding_2.md.
import { UserUtils } from '@/domain/utils/userUtils';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { memoryStorage } from '../local/memoryStorage';
import { env } from '@/config/env';

export class CloudStorageService {
  private static instance: CloudStorageService;

  public static getInstance(): CloudStorageService {
    if (!CloudStorageService.instance) {
      CloudStorageService.instance = new CloudStorageService();
    }
    return CloudStorageService.instance;
  }

  /**
   * Download a file from a GCS bucket URL as a Blob.
   * Uses direct storage.googleapis.com URLs (not CDN) to avoid CORS issues.
   * Requires CORS to be configured on the target bucket via gsutil.
   *
   * @param url - Full GCS URL (e.g. '${env.CDN_BUCKET_PREFIX}media/...' or '${env.USER_PROFILES_BUCKET_PREFIX}id/id')
   * @returns Blob or null on failure
   */
  async downloadFile(url: string): Promise<Blob | null> {
    return memoryStorage.downloadImage(url);
  }

  /**
   * Upload user profile image to Firebase Storage.
   *
   * Faithful port of Flutter's `AppStorageService.uploadUserProfileImage(String? profileImageUrl)`
   * (lib/services/app_storage_service.dart:371-403):
   *   - `:374` null/empty guard → null;
   *   - `:375` an `http`-prefixed URL is downloaded first (imported from Firebase
   *     Auth, e.g. a Google avatar), otherwise `:387-395` the local file is
   *     compressed — both arms are resolved here by
   *     `memoryStorage.downloadUserProfileImage` (see its parity note for the one
   *     deliberate web-vs-Flutter divergence on which arm compresses);
   *   - `:398-400` any throw collapses to `null`.
   *
   * A `null` return means "upload failed" — it is NOT "no change", and it must
   * never be persisted: `profile_image` is written through the `onboardUser`
   * callable's `merge` set, so storing the null WIPES the stored avatar with no
   * error surfaced (the callable itself succeeded). Deciding what a failed upload
   * MEANS stays with the domain caller; both callers refuse to persist it:
   *   - `EditUserProfileUseCase` guards on `changedProfileImage &&
   *     !uploadedProfileImage` and ABORTS the save — no `onboardUser` call,
   *     nothing persisted — returning `success: false` so the page's save-error
   *     sheet fires and the existing avatar survives;
   *   - `syncSecureStorageUseCase.syncUploadProfileImage` returns early without
   *     calling `saveUserProfileImage`. It is the precedent the edit-profile
   *     guard follows.
   *
   * This is a DELIBERATE web-vs-Flutter divergence, not a parity gap. Flutter
   * persists the null in both places (`users_repository.dart:68-71` →`:77-85`, and
   * `sync_repository.dart:601-604`); a Flutter-side fix is recommended. It is
   * tolerable there only because its failure modes are native-client rare — its
   * download arm and `putFile` are not subject to CORS. On web both are browser
   * calls behind a preflight, so the destructive path is ordinary rather than
   * exceptional.
   *
   * @param profileImageUrl - URL or path to profile image
   * @returns Short storage path (`expause-profiles%2F{uid}`) or null on failure
   */
  async uploadUserProfileImage(profileImageUrl: string | null | undefined): Promise<string | null> {
    if (!profileImageUrl) {
      return null;
    }

    try {
      // Download and process the image (handles both remote and local URLs, plus compression)
      const processedBlob = await memoryStorage.downloadUserProfileImage(profileImageUrl);

      if (!processedBlob) {
        appLogger.error('Failed to process profile image', 'CloudStorageService.uploadUserProfileImage');
        return null;
      }

      // Upload to user profiles bucket
      return await this.uploadFileToUserProfilesBucket(processedBlob);
    } catch (error) {
      appLogger.error('Error uploading profile image', 'CloudStorageService.uploadUserProfileImage', { error: error as Error });
      return null;
    }
  }

  /**
   * Upload a single feedback screenshot to the default Firebase Storage bucket
   * at the provided `uploadPath` and return its bucket-relative short path — the
   * value persisted as an `in_app_feedback` doc's `screenshots[].path`.
   *
   * Faithful port of Flutter's
   * `AppStorageService.uploadFeedbackScreenshot(String filePath)`
   * (lib/services/app_storage_service.dart:405-428):
   *   - uploads to `feedback/{uid}/{millis}` with `contentType: image/jpeg`;
   *   - reads the object back via `getDownloadURL` (the feedback read side needs
   *     the resolved URL, so this uses the shared `uploadWithDownloadUrlReadback`
   *     read-back path, unlike the community-image no-read-back upload);
   *   - returns `downloadUrl.replaceFirst(Env.defaultBucketPrefix, '')`.
   *
   * The `uploadPath` (`feedback/{uid}/{millis}`) is built in the DOMAIN layer
   * (Task 3) — this proxy does NOT self-resolve the uid or stamp the timestamp,
   * per the data-layer surface rule.
   *
   * Persisted-path shape (cross-client, load-bearing): Flutter's
   * `Env.defaultBucketPrefix` ends at `…/<bucket>/` (NO `o/` segment), so its
   * `replaceFirst` leaves a leading `o/` (e.g. `o/feedback%2F{uid}%2F{ms}?alt=media&token=…`).
   * The web `env.DEFAULT_BUCKET_PREFIX` instead ends at `…/o/`, so stripping the
   * full web prefix would drop the `o/` and yield a path Flutter renders as a
   * broken URL. To reproduce Flutter's stored shape we strip only up to
   * `…/<bucket>/` (exclusive of `o/`) by removing the trailing `o/` from the web
   * prefix, keeping the leading `o/`. The read side re-hydrates via
   * `MediaUtils.joinDefaultBucketUrl` (mediaUtils.ts:46-61), which normalises to
   * exactly one `o/` segment for both prefix shapes — the exact inverse of this.
   *
   * `String.prototype.replace` with a string needle replaces only the first
   * occurrence — the direct equivalent of Dart's `replaceFirst` — and returns
   * the URL unchanged if the prefix is absent (same as Dart).
   *
   * Image compression (Flutter compresses the source to width 540 in the
   * repository BEFORE calling this) is a domain-layer concern; this proxy
   * uploads the provided blob as-is.
   *
   * @param file - The (already-compressed) screenshot image blob.
   * @param uploadPath - Domain-built bucket-relative path (`feedback/{uid}/{millis}`).
   * @param contentType - MIME content type (`image/jpeg`).
   * @returns The bucket-relative short path (leading `o/`), or null on failure.
   */
  async uploadFeedbackScreenshot(
    file: Blob,
    uploadPath: string,
    contentType: string,
  ): Promise<string | null> {
    try {
      const { downloadUrl } = await this.uploadWithDownloadUrlReadback(
        file,
        uploadPath,
        contentType,
        'CloudStorageService.uploadFeedbackScreenshot',
      );
      const bucketPrefix = env.DEFAULT_BUCKET_PREFIX.endsWith('o/')
        ? env.DEFAULT_BUCKET_PREFIX.slice(0, -2)
        : env.DEFAULT_BUCKET_PREFIX;
      return downloadUrl.replace(bucketPrefix, '');
    } catch (error) {
      appLogger.error(
        'Error uploading feedback screenshot',
        'CloudStorageService.uploadFeedbackScreenshot',
        { error: error as Error },
      );
      return null;
    }
  }

  /**
   * Upload a user-authored media file (video) to the default Firebase Storage bucket
   * at the provided path and report progress. Matches Flutter's
   * `AppStorageService._uploadUserVideoAndTriggerTranscode` upload step.
   *
   * @param file - File/Blob to upload
   * @param uploadPath - Full bucket-relative path (e.g. `media/{uid}/{timestamp}`)
   * @param contentType - MIME content type (e.g. `video/mp4`)
   * @param onProgress - Optional callback receiving 0-100 percent
   * @returns Download URL for the uploaded file
   */
  async uploadUserMediaContent(
    file: Blob,
    uploadPath: string,
    contentType: string,
    { onProgress }: { onProgress?: (percent: number) => void } = {},
  ): Promise<{ downloadUrl: string }> {
    const storageRef: StorageReference = ref(storage, uploadPath);
    const task = uploadBytesResumable(storageRef, file, { contentType });
    let lastReportedPercent = -1;

    return new Promise((resolve, reject) => {
      task.on(
        'state_changed',
        (snapshot) => {
          const { bytesTransferred, totalBytes } = snapshot;
          if (totalBytes > 0 && onProgress) {
            const percent = Math.floor((bytesTransferred / totalBytes) * 100);
            if (percent !== lastReportedPercent) {
              lastReportedPercent = percent;
              onProgress(percent);
            }
          }
        },
        (error) => {
          appLogger.error(
            'Error uploading user media content',
            'CloudStorageService.uploadUserMediaContent', { error: error as Error },
          );
          reject(error);
        },
        async () => {
          try {
            const downloadUrl = await getDownloadURL(task.snapshot.ref);
            resolve({ downloadUrl });
          } catch (error) {
            appLogger.error(
              'Error getting download URL',
              'CloudStorageService.uploadUserMediaContent', { error: error as Error },
            );
            reject(error);
          }
        },
      );
    });
  }

  /**
   * Upload a chat-media file to the default Firebase Storage bucket at the
   * provided path and return its download URL. Matches the upload step of
   * Flutter's `AppStorageService.uploadChatMedia`
   * (lib/services/app_storage_service.dart:191-196): an explicit content type
   * and NO progress listener (the chat send flow shows no uploading banner).
   *
   * Uses `uploadBytesResumable` (mirroring `uploadUserMediaContent`) for two
   * reasons: resumable uploads retry on flaky connections, and the resolved
   * `getDownloadURL` lets the caller send the URL as `dynamicUrl` on the
   * transcode payload so the Cloud Function can ffprobe the REMOTE file's
   * metadata instead of downloading the whole video.
   *
   * @param file - File/Blob to upload
   * @param uploadPath - Full bucket-relative path (e.g. `chat/{groupChatId}/{fileName}`)
   * @param contentType - MIME content type (e.g. `video/mp4`, `image/jpeg`)
   * @returns Download URL for the uploaded file
   */
  async uploadChatMediaContent(
    file: Blob,
    uploadPath: string,
    contentType: string,
  ): Promise<{ downloadUrl: string }> {
    return this.uploadWithDownloadUrlReadback(
      file,
      uploadPath,
      contentType,
      'CloudStorageService.uploadChatMediaContent',
    );
  }

  /**
   * Upload a community-message media file to the default Firebase Storage
   * bucket at the provided path. Matches the upload step of Flutter's
   * `AppStorageService._uploadCommunityMessageMedia`
   * (lib/services/app_storage_service.dart:240-248):
   * `FirebaseStorage.instance.ref(uploadPath).putFile(file, SettableMetadata(contentType: ...))`
   * — an explicit content type and NO progress listener (the community upload
   * shows no uploading banner), and NO download-URL read-back.
   *
   * Resolves on upload completion (log + reject on failure only). Unlike
   * `uploadChatMediaContent`, this deliberately does NOT read the object back
   * via `getDownloadURL`: the community image caller never consumes a download
   * URL (the transcode CF downloads the object it minted at `uploadPath` — it
   * does not ffprobe a remote URL), and a read-after-write would put a second,
   * failable network round-trip on the success-critical path. A `getDownloadURL`
   * failure after a successful upload would invert a succeeded upload into an
   * `uploaded: false` result, leaving a message doc stuck rendering "Processing"
   * forever on both clients (the CF that clears `isUploading` never fires). This
   * also keeps parity with Flutter, whose community upload is a bare `putFile`
   * with no read-back. The community-VIDEO flow, which DOES need a download URL
   * (the transcode CF ffprobes the remote URL), uses the dedicated
   * `uploadCommunityVideoContent` method below instead of this one.
   *
   * `uploadPath` shape depends on the caller. When the object will be sent as a
   * transcode-CF `filePath` (the raw media upload — community image, and the
   * community video raw upload), it MUST be the 3-segment
   * `community/{communityOwner}/{timestampMillis}` form asserted by
   * `transcode_community_message_image.ts:32-39` (the video CF asserts the same
   * shape at `transcode_community_message_video.ts:95-102`,
   * `filePath.split('/').length == 3`). The community-VIDEO **thumbnail** caller
   * (`CommunityService.uploadCommunityVideo` step 3) instead passes the 4-segment
   * `community/{communityOwner}/thumbnail/{fileName}` path derived by
   * `MediaUtils.getCommunityVideoThumbnailUploadPath` (mediaUtils.ts:475-479);
   * that object is never sent as a CF `filePath` — it deliberately mirrors the
   * CF-side derivation (`transcode_community_message_video.ts:112-117`,
   * `{fileDir}thumbnail/{baseFileName}`) so the CF's `exists()` check finds it.
   * Either way the path is built in the domain layer, not here.
   *
   * @param file - File/Blob to upload
   * @param uploadPath - Full bucket-relative path (raw media:
   *   `community/{communityOwner}/{timestampMillis}`; video thumbnail:
   *   `community/{communityOwner}/thumbnail/{fileName}`)
   * @param contentType - MIME content type (e.g. `image/jpeg`)
   */
  async uploadCommunityMediaContent(
    file: Blob,
    uploadPath: string,
    contentType: string,
  ): Promise<void> {
    const storageRef: StorageReference = ref(storage, uploadPath);
    const task = uploadBytesResumable(storageRef, file, { contentType });

    return new Promise((resolve, reject) => {
      task.on(
        'state_changed',
        undefined,
        (error) => {
          appLogger.error(
            'Error uploading community media content',
            'CloudStorageService.uploadCommunityMediaContent',
            { error: error as Error },
          );
          reject(error);
        },
        () => resolve(),
      );
    });
  }

  /**
   * Upload a community-message VIDEO file to the default Firebase Storage bucket
   * at the provided path and return its download URL. Community-scoped mirror of
   * `uploadChatMediaContent`: the community video pipeline follows the chat video
   * flow exactly (raw upload → Google Transcoder → per-message encryption →
   * Media CDN), so unlike the community IMAGE upload
   * (`uploadCommunityMediaContent`) this DOES read the object back via
   * `getDownloadURL`.
   *
   * The resolved, tokenized (`%2F`-encoded, token-bearing) download URL becomes
   * the transcode payload's `dynamicUrl`, which the community transcode Cloud
   * Function ffprobes REMOTELY (headers only — it never downloads the full
   * video) to read the source metadata before starting the Transcoder job. See
   * the chat-video precedent `transcode_chat_video.ts:66-74`, whose
   * remote-ffprobe rationale the community pipeline reuses verbatim. Uses
   * `uploadBytesResumable` (mirroring `uploadChatMediaContent`) so the upload
   * retries on flaky connections.
   *
   * NOTE: only the raw video is uploaded through this method. The plaintext
   * video THUMBNAIL does NOT use this method — it needs no download URL because
   * the transcode Cloud Function mints its own token for the thumbnail object
   * (Task 6), so the thumbnail goes through the no-read-back
   * `uploadCommunityMediaContent` path instead.
   *
   * @param file - File/Blob to upload
   * @param uploadPath - Full bucket-relative path (`community/{communityOwner}/{timestampMillis}`)
   * @param contentType - MIME content type (e.g. `video/mp4`)
   * @returns Download URL for the uploaded video
   */
  async uploadCommunityVideoContent(
    file: Blob,
    uploadPath: string,
    contentType: string,
  ): Promise<{ downloadUrl: string }> {
    return this.uploadWithDownloadUrlReadback(
      file,
      uploadPath,
      contentType,
      'CloudStorageService.uploadCommunityVideoContent',
    );
  }

  /**
   * Shared resumable-upload + download-URL read-back used by the chat and
   * community VIDEO upload paths (both feed the transcode CF's remote-ffprobe
   * `dynamicUrl`). `logContext` keys the appLogger source so failures stay
   * attributable to the calling feature.
   *
   * @param file - File/Blob to upload
   * @param uploadPath - Full bucket-relative path
   * @param contentType - MIME content type (e.g. `video/mp4`, `image/jpeg`)
   * @param logContext - appLogger source of the calling public method
   * @returns Download URL for the uploaded file
   */
  private uploadWithDownloadUrlReadback(
    file: Blob,
    uploadPath: string,
    contentType: string,
    logContext: string,
  ): Promise<{ downloadUrl: string }> {
    const storageRef: StorageReference = ref(storage, uploadPath);
    const task = uploadBytesResumable(storageRef, file, { contentType });

    return new Promise((resolve, reject) => {
      task.on(
        'state_changed',
        undefined,
        (error) => {
          appLogger.error('Error uploading media content', logContext, { error: error as Error });
          reject(error);
        },
        async () => {
          try {
            const downloadUrl = await getDownloadURL(task.snapshot.ref);
            resolve({ downloadUrl });
          } catch (error) {
            appLogger.error('Error getting download URL', logContext, { error: error as Error });
            reject(error);
          }
        },
      );
    });
  }

  /**
   * Resolve a tokenized, %2F-encoded Firebase Storage download URL from a
   * bucket-relative object path (e.g. `chat/{group}/thumbnail/{ts}`).
   * Mirrors the URL the upload side returns via `getDownloadURL` so the
   * encrypted-media read path fetches a valid (percent-encoded, token-bearing)
   * URL instead of concatenating the raw object path onto the bucket prefix
   * (which the Firebase Storage REST endpoint rejects with HTTP 400).
   *
   * @param relativePath - Bucket-relative object path (no leading slash)
   * @returns Download URL, or null on failure (e.g. object missing).
   */
  async getDownloadUrl(relativePath: string): Promise<string | null> {
    try {
      return await getDownloadURL(ref(storage, relativePath));
    } catch (error) {
      appLogger.error(
        'Error resolving storage download URL',
        'CloudStorageService.getDownloadUrl',
        { error: error as Error },
      );
      return null;
    }
  }

  /**
   * Upload file to the user profiles bucket.
   *
   * Faithful port of Flutter's `AppStorageService._uploadFileToUserProfilesBucket(File file)`
   * (lib/services/app_storage_service.dart:296-329): resolve the current Firebase
   * user (`:298-301`, null when unauthenticated), `putFile` to the profiles bucket
   * with `contentType: image/jpeg` + `cacheControl: no-cache` (`:306-315`), return
   * the short image URL (`:319-321`), swallow any error → null (`:322-326`).
   *
   * Two cross-client contracts are load-bearing here:
   *
   * (a) The object path is the FIXED 3-segment `profile/{uid}/{uid}`
   *     (`:304-305`) — never timestamped. A re-upload therefore OVERWRITES IN
   *     PLACE at a URL that never changes, which is why this upload sets
   *     `cacheControl: no-cache` AND why the caller evicts the profile image from
   *     the local image cache after a successful re-upload (Flutter:
   *     `users_repository.dart:73-75` → `cache_utils.dart:42-56`). Without both,
   *     a changed avatar keeps rendering the stale bytes.
   *
   * (b) The returned short form `expause-profiles%2F{uid}` — NOT a full URL — is
   *     what lands in Firestore `profile_image`. It must keep containing
   *     `env.USER_PROFILES_BUCKET_NAME`, because Flutter's
   *     `User.profileImageUploaded` getter (lib/data/models/user.dart:108-110)
   *     tests exactly `profileImage!.contains(Env.userProfilesBucketName)`. Break
   *     that substring and Flutter stops recognising a web-authored avatar.
   *
   * The bucket handle resolves from `env.USER_PROFILES_BUCKET_NAME` rather than a
   * hardcoded literal, mirroring Flutter's own `Env.userProfilesBucketName`
   * indirection (lib/env/env.dart:29-30) and keeping this upload target in lockstep
   * with the `UserUtils.generateUserProfileShortImageUrl` return value, which reads
   * the same env var. (Flutter hardcodes the literal at `:306` despite owning the
   * env field; the web does not reproduce that drift risk.)
   *
   * @param file - Blob to upload
   * @returns Short storage path (`expause-profiles%2F{uid}`) or null on failure
   */
  private async uploadFileToUserProfilesBucket(file: Blob): Promise<string | null> {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      appLogger.error('No authenticated user for profile upload', 'CloudStorageService.uploadFileToUserProfilesBucket');
      return null;
    }

    try {
      const firebaseId = currentUser.uid;

      // Get reference to profiles bucket (matching Flutter's Env.userProfilesBucketName)
      const profilesStorage = getStorage(undefined, `gs://${env.USER_PROFILES_BUCKET_NAME}`);

      // Upload path: profile/{userId}/{userId}
      const uploadPath = `profile/${firebaseId}/${firebaseId}`;
      const storageRef: StorageReference = ref(profilesStorage, uploadPath);

      // Upload with metadata (matching Flutter's SettableMetadata)
      await uploadBytes(storageRef, file, {
        contentType: 'image/jpeg',
        cacheControl: 'no-cache',
      });

      // Return short path (bucket name only, computed based on user ID)
      // This matches Flutter's TextUtils.generateUserProfileShortImageUrl
      return UserUtils.generateUserProfileShortImageUrl(firebaseId);
    } catch (error) {
      appLogger.error('Error uploading to profiles bucket', 'CloudStorageService.uploadFileToUserProfilesBucket', { error: error as Error });
      return null;
    }
  }
}

// Export singleton instance for convenience
export const cloudStorageService = CloudStorageService.getInstance();
